import { Location } from "@angular/common";
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { ActivatedRoute, Router } from "@angular/router";
import { saveAs } from "file-saver";
import * as JSZip from "jszip";
import { NgxMasonryOptions } from "ngx-masonry";
import { ToastrService } from "ngx-toastr";
import { DownloadService } from "src/app/shared/services/download.service";
import { TranslateService } from "src/app/shared/services/translate.service";
import { environment } from "src/environments/environment";
import { DigitalMemoryLogService } from "../../services/digital-memory-log.service";
import { DownloadProgress } from "../file-detail/components/download-progress/download-progress.component";
import { PreviewTempDataService } from "../../services/preview-temp-data.service";
import AppShareHelper from "src/app/shared/utils/helper";

@Component({
  selector: "app-preview",
  templateUrl: "./preview.component.html",
  styleUrls: ["./preview.component.scss"],
})
export class PreviewComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild("videoPlayer") videoPlayer!: ElementRef<HTMLVideoElement>;
  @ViewChild("wallpaperZone") wallpaperZone: any;

  isPlaying = false;
  isMuted = false;
  currentTime = 0;
  duration = 0;
  progressPercent = 0;
  volume = 100;
  showControls = false;
  showVolumeSlider = false;
  showSettings = false;
  showSpeedSubmenu = false;
  showQualitySubmenu = false;
  currentSpeed = 1;
  currentQuality = "Auto";

  playbackSpeeds = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 2];
  videoQualities = ["Auto", "1080p", "720p", "480p", "360p"];

  previewData: any = null;
  loading = false;

  // Wallpapers data from API
  wallpapers: any[] = [];

  // Content availability check
  hasContent = false;

  // Download progress properties
  downloadProgress: DownloadProgress = {
    current: 0,
    total: 0,
    percentage: 0,
    currentFile: "",
    status: "idle",
    message: "",
  };
  showDownloadProgress = false;
  downloadCancelled = false;

  isCheckBack = false;

  isTranslated = false;
  translating = false;
  private originalPreviewData: any = null;

  // Biến lưu trữ các ID từ URL
  urlIds = {
    overviewId: "",
    previewId: "",
    fullUrl: "",
  };

  // Biến lưu trữ query parameters
  queryParams = {
    name: "",
    type: "",
  };

  // AbortController for cancelling downloads
  private downloadAbortController: AbortController | null = null;

  // Masonry options for full width 3 columns
  public renderImageOptions: NgxMasonryOptions = {
    transitionDuration: "0.3s",
    itemSelector: ".grid-item",
    horizontalOrder: true,
    fitWidth: false, // Disable fitWidth to use full width
    percentPosition: true,
    gutter: 8,
    columnWidth: ".grid-item",
  };

  private resizeTimeout: any;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private digitalMemoryLogService: DigitalMemoryLogService,
    private downloadService: DownloadService,
    private toastr: ToastrService,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
    private previewTempDataService: PreviewTempDataService,
    private translateService: TranslateService
  ) {
    // Khởi tạo urlIds để tránh lỗi null
    this.urlIds = {
      overviewId: "",
      previewId: "",
      fullUrl: "",
    };
  }

  ngOnInit() {
    if (this.router.url.includes("/overview")) {
      this.isCheckBack = true;
    }
    this.urlIds.fullUrl = this.router.url;

    // Lấy query parameters trước
    this.route.queryParams.subscribe(queryParams => {
      this.queryParams.name = queryParams["name"] || "";
      this.queryParams.type = queryParams["type"] || "";
    });

    // Lấy route params
    this.route.params.subscribe(params => {
      this.urlIds.overviewId = params["shipId"]; // Changed from 'id' to 'shipId'
      this.urlIds.previewId = params["previewId"];

      // Get previewName from custom matcher if available
      const previewName = params["previewName"];
      if (previewName) {
        // You can store previewName for use in the component if needed
      }

      // Get shipName from custom matcher if available
      const shipName = params["shipName"];
      if (shipName) {
        // Store shipName for use in the component if needed
      }

      if (this.urlIds.previewId) {
        // Check query params để xác định nguồn data
        const queryType = this.route.snapshot.queryParams["type"] || "";

        // Nếu type là 'edit' hoặc 'create', load data từ storage
        if (queryType === "edit" || queryType === "create") {
          this.loadPreviewDataFromStorage(this.urlIds.previewId);
        } else {
          // Nếu không, load từ API như bình thường
          this.loadPreviewData(this.urlIds.previewId);
        }
      }
    });

    // Add resize listener
    window.addEventListener("resize", this.handleResize.bind(this));
  }

  ngAfterViewInit() {
    requestAnimationFrame(() => {
      this.reloadMasonryLayout();
    });
  }

  loadPreviewData(id: string) {
    this.loading = true;
    this.digitalMemoryLogService.getPreviewById(id).subscribe(
      (res: any) => {
        this.previewData = res || null;
        this.loading = false;
        localStorage.setItem("currentPreviewId", id);
        this.updateBrandSettings(res);
        this.wallpapers = res.wallpapers || [];

        // Cập nhật trạng thái content availability
        this.updateContentAvailability();

        requestAnimationFrame(() => {
          this.reloadMasonryLayout();
        });
      },
      error => {
        this.loading = false;
        this.previewData = null;
        this.hasContent = false;
      }
    );
  }

  /**
   * Load preview data từ storage (khi preview từ create/edit)
   */
  loadPreviewDataFromStorage(id: string) {
    this.loading = true;

    try {
      const tempData = this.previewTempDataService.getTempPreviewData(id);

      if (tempData) {
        // Fix: Đảm bảo courses luôn là array
        // Nếu courses là object (có numeric keys), convert về array
        if (tempData.courses && !Array.isArray(tempData.courses)) {
          // Convert object với numeric keys thành array
          const coursesObject = tempData.courses;
          tempData.courses = Object.keys(coursesObject)
            .filter(key => !isNaN(Number(key)))
            .sort((a, b) => Number(a) - Number(b))
            .map(key => coursesObject[key]);
        } else if (!tempData.courses) {
          tempData.courses = [];
        }

        this.previewData = tempData;
        this.loading = false;
        localStorage.setItem("currentPreviewId", id);
        this.updateBrandSettings(tempData);
        this.wallpapers = tempData.wallpapers || [];

        // Cập nhật trạng thái content availability
        this.updateContentAvailability();

        requestAnimationFrame(() => {
          this.reloadMasonryLayout();
        });
      } else {
        // Nếu không có data trong storage, fallback về API
        this.loadPreviewData(id);
      }
    } catch (error) {
      // Fallback về API nếu có lỗi
      this.loadPreviewData(id);
    }
  }

  retryLoad() {
    if (this.urlIds.previewId) {
      this.loadPreviewData(this.urlIds.previewId);
    }
  }

  private updateBrandSettings(data: any): void {
    if (data.brandColor) {
      this.updateBrandColor(data.brandColor);
    }

    if (data.brandFont) {
      this.updateBrandFont(data.brandFont);
    }
  }

  togglePlay(video: HTMLVideoElement) {
    if (video.paused) {
      video.play();
      this.isPlaying = true;
    } else {
      video.pause();
      this.isPlaying = false;
    }
  }

  updateProgress() {
    const video = this.videoPlayer.nativeElement;
    this.currentTime = video.currentTime;
    this.progressPercent = (video.currentTime / video.duration) * 100;
  }

  onVideoLoad() {
    const video = this.videoPlayer.nativeElement;
    this.duration = video.duration;
  }

  seekVideo(event: MouseEvent) {
    const video = this.videoPlayer.nativeElement;
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const width = rect.width;
    const seekTime = (clickX / width) * video.duration;
    video.currentTime = seekTime;
  }

  toggleMute() {
    const video = this.videoPlayer.nativeElement;
    video.muted = !video.muted;
    this.isMuted = video.muted;
  }

  setVolume(event: Event) {
    const video = this.videoPlayer.nativeElement;
    const value = (event.target as HTMLInputElement).value;
    video.volume = parseInt(value) / 100;
    this.volume = parseInt(value);
  }

  formatTime(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  }

  hideControls() {
    setTimeout(() => {
      this.showControls = false;
    }, 2000);
  }

  toggleFullscreen() {
    const video = this.videoPlayer.nativeElement;
    if (video.requestFullscreen) {
      video.requestFullscreen();
    } else if ((video as any).webkitRequestFullscreen) {
      (video as any).webkitRequestFullscreen();
    } else if ((video as any).msRequestFullscreen) {
      (video as any).msRequestFullscreen();
    }
  }

  toggleSettings() {
    this.showSettings = !this.showSettings;
    this.showSpeedSubmenu = false;
    this.showQualitySubmenu = false;
    this.showVolumeSlider = false;
  }

  toggleSpeedSubmenu() {
    this.showSpeedSubmenu = true;
    this.showSettings = false;
  }

  toggleQualitySubmenu() {
    this.showQualitySubmenu = true;
    this.showSettings = false;
  }

  closeSpeedSubmenu() {
    this.showSpeedSubmenu = false;
    this.showSettings = true;
  }

  closeQualitySubmenu() {
    this.showQualitySubmenu = false;
    this.showSettings = true;
  }

  setPlaybackSpeed(speed: number) {
    const video = this.videoPlayer.nativeElement;
    video.playbackRate = speed;
    this.currentSpeed = speed;
    this.showSpeedSubmenu = false;
    this.showSettings = false;
  }

  setQuality(quality: string) {
    this.currentQuality = quality;
    // In a real implementation, you would switch video sources here
    // For now, we'll just update the current quality
    this.showQualitySubmenu = false;
    this.showSettings = false;
  }

  // TrackBy function for wallpapers
  trackByWallpaper(index: number, item: any): number {
    return item.id || index;
  }

  // Handle wallpaper click
  onWallpaperClick(wallpaper: any) {
    if (!wallpaper) {
      this.toastr.error("Wallpaper not available", "Error");
      return;
    }

    // Try multiple possible URL sources
    const imageUrl = wallpaper.url;
    if (imageUrl && imageUrl.trim() !== "") {
      window.open(imageUrl, "_blank");
    } else {
      this.toastr.error("Image URL not available", "Error");
    }
  }

  // Handle image loading error
  onImageError(event: any, wallpaper: any) {
    const img = event.target;
    const fallbackUrls = [
      wallpaper.url,
      "assets/images/placeholder-image.jpg", // Default placeholder
    ];

    const currentSrc = img.src;
    const nextUrl = fallbackUrls.find(url => url && url !== currentSrc);

    if (nextUrl) {
      img.src = nextUrl;
    } else {
      img.style.display = "none";
    }
  }

  // Method to reload masonry layout
  reloadMasonryLayout() {
    if (this.wallpaperZone && this.wallpaperZone.layout) {
      requestAnimationFrame(() => {
        this.cdr.detectChanges();
        this.wallpaperZone.layout();
      });
    }
  }

  // Method to handle image load and reload masonry
  onImageLoad() {
    requestAnimationFrame(() => {
      this.reloadMasonryLayout();
    });
  }

  async downloadAnimalPOIs(pdfUrl: string, preferredFileName?: string): Promise<void> {
    if (!pdfUrl) {
      this.toastr.error("PDF URL not available", "Error");
      return;
    }
    this.downloadAbortController = new AbortController();

    this.initializeDownloadProgress(1);
    const filename = preferredFileName || pdfUrl.split("/").pop() || "download.pdf";
    try {
      // Trường hợp photo thì vẫn có thể dùng stream để show % như file thường
      const response = await fetch(pdfUrl, { signal: this.downloadAbortController.signal });
      if (!response.ok || !response.body) {
        throw new Error(`Failed to fetch file: ${response.statusText}`);
      }

      const contentLength = parseInt(response.headers.get("Content-Length") || "0", 10);
      const reader = response.body.getReader();
      let received = 0;
      const chunks: Uint8Array[] = [];

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        if (value) {
          chunks.push(value);
          received += value.length;

          if (contentLength > 0) {
            const percent = Math.round((received / contentLength) * 100);
            this.updateSingleFileProgress("downloading", `Downloading ${filename}... (${percent}%)`, percent);
          } else {
            this.updateDownloadProgress(0, 1, `Downloaded ${received} bytes...`);
          }
        }
      }

      const blob = new Blob(chunks as any, { type: response.headers.get("Content-Type") || "application/pdf" });

      this.updateSingleFileProgress("completed", `Downloaded ${filename}`);
      this.completeDownload("Download completed successfully");
      this.toastr.success(`${filename} downloaded successfully`);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || "download.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      if (error.message === "Download cancelled" || error.name === "AbortError") {
        this.onCancelDownload();
      } else {
        this.completeDownload("Download failed");
        this.toastr.error("Failed to download file");
      }
    }
  }

  private updateSingleFileProgress(
    stage: "preparing" | "downloading" | "completed",
    filename: string,
    percent?: number
  ): void {
    let percentage = 0;
    let current = 0;

    switch (stage) {
      case "preparing":
        percentage = 0;
        current = 0;
        break;
      case "downloading":
        percentage = percent;
        current = 0;
        break;
      case "completed":
        percentage = 100;
        current = 1;
        break;
    }

    // Create a new object to trigger change detection
    this.downloadProgress = {
      ...this.downloadProgress,
      current: current,
      total: 1,
      percentage: percentage,
      currentFile: filename,
      status: "downloading",
    };
  }

  // PDF viewer methods
  getSafePDFUrl(pdfUrl: string): SafeResourceUrl {
    if (!pdfUrl) {
      return this.sanitizer.bypassSecurityTrustResourceUrl("");
    }
    return this.sanitizer.bypassSecurityTrustResourceUrl(pdfUrl + "#toolbar=1&navpanes=1&scrollbar=1");
  }

  downloadAllWallpapers() {
    if (!this.wallpapers || this.wallpapers.length === 0) {
      this.toastr.warning("No wallpapers available to download", "Warning");
      return;
    }

    if (this.wallpapers.length === 1) {
      this.downloadSingleWallpaper(this.wallpapers[0]);
    } else {
      this.downloadWallpapersAsZip(this.wallpapers);
    }
  }

  onCancelDownload(): void {
    this.downloadCancelled = true;
    if (this.downloadAbortController) {
      this.downloadAbortController.abort();
      this.downloadAbortController = null;
    }
    this.showDownloadProgress = false;
    this.downloadProgress.status = "idle";
  }

  /** Get all media items from a section (all subSections' media flattened). */
  getSectionMedia(section: any): any[] {
    if (!section || !section.subSections || !Array.isArray(section.subSections)) {
      return [];
    }
    return section.subSections.flatMap((sub: any) => (sub.media || []).filter((m: any) => m && m.url));
  }

  getSectionMediaCount(section: any): number {
    return this.getSectionMedia(section).length;
  }

  /** Get media from a single subsection. */
  getSubSectionMedia(subSection: any): any[] {
    if (!subSection || !subSection.media || !Array.isArray(subSection.media)) {
      return [];
    }
    return subSection.media.filter((m: any) => m && m.url);
  }

  getSubSectionMediaCount(subSection: any): number {
    return this.getSubSectionMedia(subSection).length;
  }

  onDownloadSubSectionClick(subSection: any, type: "photo" | "file" | "video" | "media"): void {
    const files = this.getSubSectionMedia(subSection);
    if (!files || files.length === 0) {
      this.toastr.warning("No files available to download in this subsection", "Warning");
      return;
    }
    const subName = subSection.title || subSection.name || "subsection";
    if (files.length === 1 && type === "photo") {
      this.downloadSectionSingleFile(files[0], "photo");
    } else if (files.length === 1 && (type === "file" || type === "video" || type === "media")) {
      this.downloadSectionSingleFile(files[0], "file");
    } else if (type === "photo") {
      this.downloadSectionPhotosAsMultipleZips(files, subName);
    } else {
      this.downloadSectionAsZip(files, subName);
    }
  }

  /** Get all sections for a block type (photo / video / file / media). */
  private getBlockSections(type: "photo" | "video" | "file" | "media"): any[] {
    if (!this.previewData) return [];
    const key =
      type === "photo"
        ? "photoSections"
        : type === "video"
          ? "videoSections"
          : type === "media"
            ? "mediaSections"
            : "fileSections";
    const sections = this.previewData[key];
    return Array.isArray(sections) ? sections.filter((s: any) => s && s.display) : [];
  }

  /** Get all media from all sections of a block type. */
  getBlockMedia(type: "photo" | "video" | "file" | "media"): any[] {
    return this.getBlockSections(type).reduce(
      (acc: any[], section: any) => acc.concat(this.getSectionMedia(section)),
      []
    );
  }

  /** Get photo sub-sections with their media for "download all photos" (ordered by section → sub-section). */
  private getPhotoSubSectionsWithMedia(sections: any[]): { name: string; files: any[] }[] {
    const groups: { name: string; files: any[] }[] = [];
    sections.forEach((section: any) => {
      if (!section.subSections || !Array.isArray(section.subSections)) return;
      section.subSections.forEach((sub: any, subIndex: number) => {
        const files = this.getSubSectionMedia(sub);
        if (files.length === 0) return;
        const name = sub.title || sub.name || `subsection-${subIndex + 1}`;
        groups.push({ name, files });
      });
    });
    return groups;
  }

  getBlockMediaCount(type: "photo" | "video" | "file" | "media"): number {
    return this.getBlockMedia(type).length;
  }

  onDownloadAllBlockClick(type: "photo" | "video" | "file" | "media"): void {
    const sections = this.getBlockSections(type);
    if (!sections || sections.length === 0) {
      this.toastr.warning(`No ${type} sections available to download`, "Warning");
      return;
    }
    if (type === "photo") {
      const subSectionGroups = this.getPhotoSubSectionsWithMedia(sections);
      const totalFiles = subSectionGroups.reduce((sum, g) => sum + g.files.length, 0);
      if (totalFiles === 0) {
        this.toastr.warning("No photos available to download", "Warning");
        return;
      }
      if (totalFiles === 1) {
        const only = subSectionGroups.find(g => g.files.length === 1);
        if (only) this.downloadSectionSingleFile(only.files[0], "photo");
        return;
      }
      this.downloadAllPhotosBySubSection(subSectionGroups);
      return;
    }
    if (type === "file" || type === "video" || type === "media") {
      const allSubSections = sections.reduce((acc: any[], s: any) => acc.concat(s.subSections || []), []);
      const withMedia = allSubSections.filter(
        (sub: any) => sub.media && Array.isArray(sub.media) && sub.media.filter((m: any) => m && m.url).length > 0
      );
      if (withMedia.length === 0) {
        this.toastr.warning(`No ${type}s available to download`, "Warning");
        return;
      }
      const totalFiles = withMedia.reduce(
        (sum: number, sub: any) => sum + (sub.media || []).filter((m: any) => m && m.url).length,
        0
      );
      if (totalFiles === 1 && withMedia.length === 1) {
        const file = withMedia[0].media.filter((m: any) => m && m.url)[0];
        this.downloadSectionSingleFile(file, "file");
        return;
      }
      this.downloadBlockFilesOrVideosAsZipsPerSubSection(withMedia);
    }
  }

  /** Download multiple subsections (from block) as one ZIP per subsection. */
  private async downloadBlockFilesOrVideosAsZipsPerSubSection(subSectionsWithMedia: any[]): Promise<void> {
    const totalFiles = subSectionsWithMedia.reduce(
      (sum: number, sub: any) => sum + (sub.media || []).filter((m: any) => m && m.url).length,
      0
    );
    const totalZips = subSectionsWithMedia.length;
    this.initializeDownloadProgress(totalFiles);
    this.downloadAbortController = new AbortController();
    let globalCompleted = 0;

    try {
      for (let subIndex = 0; subIndex < subSectionsWithMedia.length; subIndex++) {
        const subSection = subSectionsWithMedia[subIndex];
        const files = (subSection.media || []).filter((m: any) => m && m.url);
        const subSectionName = subSection.title || subSection.name || `subsection-${subIndex + 1}`;
        const zip = new JSZip();

        for (let index = 0; index < files.length; index++) {
          const file = files[index];
          if (this.downloadCancelled || this.downloadAbortController!.signal.aborted) {
            throw new Error("Download cancelled");
          }
          const fetchMethod = this.downloadService.fetchFileAsBlob(file.url, this.downloadAbortController!.signal);
          const blob = await fetchMethod;
          if (this.downloadCancelled || this.downloadAbortController!.signal.aborted) {
            throw new Error("Download cancelled");
          }
          const filename = file.fileName || file.url.split("/").pop() || `file_${index + 1}`;
          zip.file(filename, blob);
          globalCompleted++;
          const progressPercent = Math.round((globalCompleted / totalFiles) * 100);
          const fileProgressText =
            totalZips > 1
              ? `Downloading files for ZIP ${subIndex + 1}/${totalZips} (${index + 1}/${files.length} files)...`
              : `Downloading (${index + 1}/${files.length} files)...`;
          this.updateDownloadProgress(globalCompleted, totalFiles, fileProgressText, progressPercent);
          this.cdr.detectChanges();
          await new Promise(r => setTimeout(r, 0));
        }

        if (this.downloadCancelled || this.downloadAbortController!.signal.aborted) {
          throw new Error("Download cancelled");
        }
        const creatingPercent = Math.round((globalCompleted / totalFiles) * 100);
        const creatingText =
          totalZips > 1 ? `Creating ZIP file ${subIndex + 1}/${totalZips}...` : "Creating ZIP file...";
        this.updateDownloadProgress(globalCompleted, totalFiles, creatingText, creatingPercent);
        this.cdr.detectChanges();
        const content = await zip.generateAsync({ type: "blob", streamFiles: true, compression: "STORE" });
        if (this.downloadCancelled || this.downloadAbortController!.signal.aborted) {
          throw new Error("Download cancelled");
        }
        const zipName = `${subSectionName}.zip`;
        saveAs(content, zipName);
        this.updateDownloadProgress(
          globalCompleted,
          totalFiles,
          `Downloaded ${zipName} (${globalCompleted}/${totalFiles} files)`,
          Math.round((globalCompleted / totalFiles) * 100)
        );
        this.cdr.detectChanges();

        if (subIndex < subSectionsWithMedia.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }

      this.completeDownload(
        totalZips > 1
          ? `Successfully downloaded ${totalZips} ZIP files (${totalFiles} files)`
          : "ZIP file created successfully"
      );
      this.toastr.success(
        totalZips > 1 ? `Successfully downloaded ${totalZips} ZIP files` : "ZIP file created successfully",
        "Success"
      );
    } catch (error) {
      if (error.message === "Download cancelled" || error.name === "AbortError") {
        this.cancelDownload();
      } else {
        this.errorDownload("Error downloading files. Please try again.");
        this.toastr.error("Error downloading files. Please try again.", "Error");
      }
    }
    this.downloadAbortController = null;
  }

  onDownloadAllSectionClick(section: any, type: "file" | "photo"): void {
    const files = this.getSectionMedia(section);
    if (!files || files.length === 0) {
      this.toastr.warning("No files available to download in this section", "Warning");
      return;
    }
    const sectionName = section.name || section.title || (type === "photo" ? "photos" : "files");
    if (files.length === 1 && type === "photo") {
      this.downloadSectionSingleFile(files[0], type);
    } else if (type === "photo") {
      this.downloadSectionPhotosAsMultipleZips(files, sectionName);
    } else if (type === "file") {
      this.downloadSectionFilesAsZipsPerSubSection(section);
    } else {
      this.downloadSectionAsZip(files, sectionName);
    }
  }

  private async downloadSectionAsZip(files: any[], sectionName: string): Promise<void> {
    this.initializeDownloadProgress(files.length);
    this.downloadAbortController = new AbortController();
    const zip = new JSZip();
    let completed = 0;

    try {
      for (let index = 0; index < files.length; index++) {
        const file = files[index];
        const fetchMethod = this.downloadService.fetchFileAsBlob(file.url, this.downloadAbortController!.signal);
        const blob = await fetchMethod;
        if (this.downloadCancelled || this.downloadAbortController!.signal.aborted) {
          throw new Error("Download cancelled");
        }
        const filename = file.fileName || file.url.split("/").pop() || `file_${index + 1}`;
        zip.file(filename, blob);
        completed++;
        this.updateDownloadProgress(completed, files.length, `Downloaded ${filename}`);
      }
      this.updateDownloadProgress(files.length, files.length, "Creating ZIP file...");
      const content = await zip.generateAsync({ type: "blob", streamFiles: true, compression: "STORE" });
      if (this.downloadCancelled || this.downloadAbortController!.signal.aborted) {
        throw new Error("Download cancelled");
      }
      const zipName = `${sectionName}.zip`;
      saveAs(content, zipName);
      this.completeDownload("ZIP file created successfully");
      this.toastr.success(`Started downloading: ${zipName}`, "Success");
    } catch (error) {
      if (error.message === "Download cancelled" || error.name === "AbortError") {
        this.cancelDownload();
      } else {
        this.errorDownload("Error downloading files. Please try again.");
        this.toastr.error("Error downloading files. Please try again.", "Error");
      }
    }
    this.downloadAbortController = null;
  }

  /** For file type: one ZIP per subsection (each subsection's media in its own ZIP). */
  private async downloadSectionFilesAsZipsPerSubSection(section: any): Promise<void> {
    const subSectionsWithMedia = (section.subSections || []).filter(
      (sub: any) => sub.media && Array.isArray(sub.media) && sub.media.filter((m: any) => m && m.url).length > 0
    );
    if (subSectionsWithMedia.length === 0) {
      this.toastr.warning("No files available to download in this section", "Warning");
      return;
    }

    const totalFiles = subSectionsWithMedia.reduce(
      (sum: number, sub: any) => sum + (sub.media || []).filter((m: any) => m && m.url).length,
      0
    );
    if (totalFiles === 1 && subSectionsWithMedia.length === 1) {
      const file = subSectionsWithMedia[0].media.filter((m: any) => m && m.url)[0];
      this.downloadSectionSingleFile(file, "file");
      return;
    }

    const totalZips = subSectionsWithMedia.length;
    this.initializeDownloadProgress(totalFiles);
    this.downloadAbortController = new AbortController();
    let globalCompleted = 0;

    try {
      for (let subIndex = 0; subIndex < subSectionsWithMedia.length; subIndex++) {
        const subSection = subSectionsWithMedia[subIndex];
        const files = (subSection.media || []).filter((m: any) => m && m.url);
        const subSectionName = subSection.title || subSection.name || `subsection-${subIndex + 1}`;
        const zip = new JSZip();

        for (let index = 0; index < files.length; index++) {
          const file = files[index];
          if (this.downloadCancelled || this.downloadAbortController!.signal.aborted) {
            throw new Error("Download cancelled");
          }
          const fetchMethod = this.downloadService.fetchFileAsBlob(file.url, this.downloadAbortController!.signal);
          const blob = await fetchMethod;
          if (this.downloadCancelled || this.downloadAbortController!.signal.aborted) {
            throw new Error("Download cancelled");
          }
          const filename = file.fileName || file.url.split("/").pop() || `file_${index + 1}`;
          zip.file(filename, blob);
          globalCompleted++;
          const progressPercent = Math.round((globalCompleted / totalFiles) * 100);
          const fileProgressText =
            totalZips > 1
              ? `Downloading files for ZIP ${subIndex + 1}/${totalZips} (${index + 1}/${files.length} files)...`
              : `Downloading (${index + 1}/${files.length} files)...`;
          this.updateDownloadProgress(globalCompleted, totalFiles, fileProgressText, progressPercent);
          this.cdr.detectChanges();
          await new Promise(r => setTimeout(r, 0));
        }

        if (this.downloadCancelled || this.downloadAbortController!.signal.aborted) {
          throw new Error("Download cancelled");
        }
        const creatingPercent = Math.round((globalCompleted / totalFiles) * 100);
        const creatingText =
          totalZips > 1 ? `Creating ZIP file ${subIndex + 1}/${totalZips}...` : "Creating ZIP file...";
        this.updateDownloadProgress(globalCompleted, totalFiles, creatingText, creatingPercent);
        this.cdr.detectChanges();
        const content = await zip.generateAsync({ type: "blob", streamFiles: true, compression: "STORE" });
        if (this.downloadCancelled || this.downloadAbortController!.signal.aborted) {
          throw new Error("Download cancelled");
        }
        const zipName = `${subSectionName}.zip`;
        saveAs(content, zipName);
        const zipCompletedPercent = Math.round((globalCompleted / totalFiles) * 100);
        this.updateDownloadProgress(
          globalCompleted,
          totalFiles,
          `Downloaded ${zipName} (${globalCompleted}/${totalFiles} files)`,
          zipCompletedPercent
        );
        this.cdr.detectChanges();

        if (subIndex < subSectionsWithMedia.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }

      const zipCount = subSectionsWithMedia.length;
      this.completeDownload(
        zipCount > 1
          ? `Successfully downloaded ${zipCount} ZIP files (${totalFiles} files)`
          : "ZIP file created successfully"
      );
      this.toastr.success(
        subSectionsWithMedia.length > 1
          ? `Successfully downloaded ${subSectionsWithMedia.length} ZIP files`
          : "ZIP file created successfully",
        "Success"
      );
    } catch (error) {
      if (error.message === "Download cancelled" || error.name === "AbortError") {
        this.cancelDownload();
      } else {
        this.errorDownload("Error downloading files. Please try again.");
        this.toastr.error("Error downloading files. Please try again.", "Error");
      }
    }
    this.downloadAbortController = null;
  }

  private static readonly IMAGES_PER_ZIP = 100;

  /**
   * Download all photos grouped by sub-section. One zip per sub-section (or multiple zips per sub-section when >100 images).
   */
  private async downloadAllPhotosBySubSection(subSectionGroups: { name: string; files: any[] }[]): Promise<void> {
    const totalFiles = subSectionGroups.reduce((sum, g) => sum + g.files.length, 0);
    this.initializeDownloadProgress(totalFiles);
    this.downloadAbortController = new AbortController();
    let totalCompletedImages = 0;

    try {
      for (let gIndex = 0; gIndex < subSectionGroups.length; gIndex++) {
        const { name: subName, files } = subSectionGroups[gIndex];
        if (files.length === 0) continue;

        if (files.length === 1) {
          if (this.downloadCancelled || this.downloadAbortController!.signal.aborted) {
            throw new Error("Download cancelled");
          }
          try {
            const blob = await this.downloadService.fetchImageAsBlob(
              files[0].url,
              this.downloadAbortController!.signal
            );
            if (this.downloadCancelled || this.downloadAbortController!.signal.aborted) {
              throw new Error("Download cancelled");
            }
            const filename = files[0].fileName || files[0].url.split("/").pop() || "image_1";
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            a.click();
            window.URL.revokeObjectURL(url);
          } catch (err) {
            console.error("Failed to download single image:", files[0].url, err);
          }
          totalCompletedImages++;
          this.updateDownloadProgress(
            totalCompletedImages,
            totalFiles,
            `Downloaded ${subName} (1 file)`,
            Math.round((totalCompletedImages / totalFiles) * 100)
          );
          this.cdr.detectChanges();
          continue;
        }

        const chunks: any[][] = [];
        for (let i = 0; i < files.length; i += PreviewComponent.IMAGES_PER_ZIP) {
          chunks.push(files.slice(i, i + PreviewComponent.IMAGES_PER_ZIP));
        }

        for (let zipIndex = 0; zipIndex < chunks.length; zipIndex++) {
          const chunk = chunks[zipIndex];
          const zip = new JSZip();
          const usedFilenames = new Set<string>();

          for (let index = 0; index < chunk.length; index++) {
            const file = chunk[index];
            if (this.downloadCancelled || this.downloadAbortController!.signal.aborted) {
              throw new Error("Download cancelled");
            }
            try {
              const blob = await this.downloadService.fetchImageAsBlob(file.url, this.downloadAbortController!.signal);
              if (this.downloadCancelled || this.downloadAbortController!.signal.aborted) {
                throw new Error("Download cancelled");
              }
              let baseFilename = file.fileName || file.url.split("/").pop() || `image_${totalCompletedImages + 1}`;
              let filename = baseFilename;
              let counter = 1;
              while (usedFilenames.has(filename)) {
                const ext = baseFilename.includes(".") ? baseFilename.substring(baseFilename.lastIndexOf(".")) : "";
                const nameWithoutExt = baseFilename.includes(".")
                  ? baseFilename.substring(0, baseFilename.lastIndexOf("."))
                  : baseFilename;
                filename = `${nameWithoutExt}_${counter}${ext}`;
                counter++;
              }
              usedFilenames.add(filename);
              zip.file(filename, blob);
            } catch (err) {
              console.error(`Failed to download image:`, file.url, err);
            }
            totalCompletedImages++;
            const progressPercent = Math.round((totalCompletedImages / totalFiles) * 100);
            const partLabel = chunks.length > 1 ? ` ${zipIndex + 1}/${chunks.length}` : "";
            this.updateDownloadProgress(
              totalCompletedImages,
              totalFiles,
              `Downloading ${subName}${partLabel} (${index + 1}/${chunk.length})...`,
              progressPercent
            );
            this.cdr.detectChanges();
            await new Promise(r => setTimeout(r, 0));
          }

          if (this.downloadCancelled || this.downloadAbortController!.signal.aborted) {
            throw new Error("Download cancelled");
          }
          const creatingPercent = Math.round((totalCompletedImages / totalFiles) * 100);
          this.updateDownloadProgress(
            totalCompletedImages,
            totalFiles,
            `Creating ZIP ${subName}${chunks.length > 1 ? ` part ${zipIndex + 1}` : ""}...`,
            creatingPercent
          );
          this.cdr.detectChanges();
          const content = await zip.generateAsync({
            type: "blob",
            streamFiles: true,
            compression: "STORE",
          });
          if (this.downloadCancelled || this.downloadAbortController!.signal.aborted) {
            throw new Error("Download cancelled");
          }
          const partSuffix = chunks.length > 1 ? `_part${zipIndex + 1}` : "";
          const zipName = `${subName}${partSuffix}.zip`;
          saveAs(content, zipName);
          this.updateDownloadProgress(
            totalCompletedImages,
            totalFiles,
            `Downloaded ${zipName}`,
            Math.round((totalCompletedImages / totalFiles) * 100)
          );
          this.cdr.detectChanges();
          if (zipIndex < chunks.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        }
      }

      const totalZips = subSectionGroups.reduce((sum, g) => {
        if (g.files.length <= 1) return sum + (g.files.length === 1 ? 1 : 0);
        return sum + Math.ceil(g.files.length / PreviewComponent.IMAGES_PER_ZIP);
      }, 0);
      this.completeDownload(`Downloaded ${totalZips} ZIP/file(s) (${totalFiles} images)`);
      this.toastr.success(totalZips > 1 ? `Downloaded ${totalZips} files` : "Download completed", "Success");
    } catch (error) {
      if (error.message === "Download cancelled" || error.name === "AbortError") {
        this.cancelDownload();
      } else {
        this.errorDownload("Error downloading images. Please try again.");
        this.toastr.error("Error downloading images. Please try again.", "Error");
      }
    }
    this.downloadAbortController = null;
  }

  private async downloadSectionPhotosAsMultipleZips(files: any[], sectionName: string): Promise<void> {
    const IMAGES_PER_ZIP = PreviewComponent.IMAGES_PER_ZIP;
    const totalZips = Math.ceil(files.length / IMAGES_PER_ZIP);
    this.initializeDownloadProgress(files.length);
    this.downloadAbortController = new AbortController();

    try {
      const chunks: any[][] = [];
      for (let i = 0; i < files.length; i += IMAGES_PER_ZIP) {
        chunks.push(files.slice(i, i + IMAGES_PER_ZIP));
      }
      let totalCompletedImages = 0;

      for (let zipIndex = 0; zipIndex < chunks.length; zipIndex++) {
        const chunk = chunks[zipIndex];
        const zip = new JSZip();
        const usedFilenames = new Set<string>();
        let completedInChunk = 0;

        for (let index = 0; index < chunk.length; index++) {
          const file = chunk[index];
          if (this.downloadCancelled || this.downloadAbortController!.signal.aborted) {
            throw new Error("Download cancelled");
          }
          try {
            const blob = await this.downloadService.fetchImageAsBlob(file.url, this.downloadAbortController!.signal);
            if (this.downloadCancelled || this.downloadAbortController!.signal.aborted) {
              throw new Error("Download cancelled");
            }
            let baseFilename = file.fileName || file.url.split("/").pop() || `image_${totalCompletedImages + 1}`;
            let filename = baseFilename;
            let counter = 1;
            while (usedFilenames.has(filename)) {
              const ext = baseFilename.includes(".") ? baseFilename.substring(baseFilename.lastIndexOf(".")) : "";
              const nameWithoutExt = baseFilename.includes(".")
                ? baseFilename.substring(0, baseFilename.lastIndexOf("."))
                : baseFilename;
              filename = `${nameWithoutExt}_${counter}${ext}`;
              counter++;
            }
            usedFilenames.add(filename);
            zip.file(filename, blob);
            completedInChunk++;
            totalCompletedImages++;
            const progressPercent = Math.round((totalCompletedImages / files.length) * 100);
            const zipProgressText =
              totalZips > 1
                ? `Downloading images for ZIP ${zipIndex + 1}/${totalZips} (${completedInChunk}/${chunk.length} images)...`
                : `Downloading (${completedInChunk}/${chunk.length} images)...`;
            this.updateDownloadProgress(totalCompletedImages, files.length, zipProgressText, progressPercent);
            this.cdr.detectChanges();
            await new Promise(r => setTimeout(r, 0));
          } catch (error) {
            console.error(`Failed to download image in chunk ${zipIndex + 1}:`, file.url, error);
            totalCompletedImages++;
          }
        }

        if (this.downloadCancelled || this.downloadAbortController!.signal.aborted) {
          throw new Error("Download cancelled");
        }
        const creatingZipPercent = Math.round((totalCompletedImages / files.length) * 100);
        this.updateDownloadProgress(
          totalCompletedImages,
          files.length,
          totalZips > 1 ? `Creating ZIP file ${zipIndex + 1}/${totalZips}...` : "Creating ZIP file...",
          creatingZipPercent
        );
        this.cdr.detectChanges();
        const content = await zip.generateAsync({ type: "blob", streamFiles: true, compression: "STORE" });
        if (this.downloadCancelled || this.downloadAbortController!.signal.aborted) {
          throw new Error("Download cancelled");
        }
        const partSuffix = totalZips > 1 ? `_part${zipIndex + 1}` : "";
        const zipName = `${sectionName}${partSuffix}.zip`;
        saveAs(content, zipName);
        const zipCompletedPercent = Math.round((totalCompletedImages / files.length) * 100);
        this.updateDownloadProgress(
          totalCompletedImages,
          files.length,
          `Downloaded ${zipName} (${totalCompletedImages}/${files.length} images)`,
          zipCompletedPercent
        );
        this.cdr.detectChanges();
        if (zipIndex < chunks.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }

      const completeMessage =
        totalZips > 1
          ? `Successfully downloaded ${totalZips} ZIP files (${files.length} images)`
          : `Successfully downloaded ZIP file (${files.length} images)`;
      this.completeDownload(completeMessage);
      this.toastr.success(
        totalZips > 1 ? `Successfully downloaded ${totalZips} ZIP files` : `Successfully downloaded ZIP file`,
        "Success"
      );
    } catch (error) {
      if (error.message === "Download cancelled" || error.name === "AbortError") {
        this.cancelDownload();
      } else {
        this.errorDownload("Error downloading images. Please try again.");
        this.toastr.error("Error downloading images. Please try again.", "Error");
      }
    }
    this.downloadAbortController = null;
  }

  private downloadSectionSingleFile(file: any, type: "file" | "photo"): void {
    if (!file || !file.url) {
      this.toastr.error("Download URL not available", "Error");
      return;
    }
    this.initializeDownloadProgress(1);
    this.downloadAbortController = new AbortController();
    const filename = file.fileName || file.url.split("/").pop() || "download";

    const fetchMethod =
      type === "photo"
        ? this.downloadService.fetchImageAsBlob(file.url, this.downloadAbortController.signal)
        : this.downloadService.fetchFileAsBlob(file.url, this.downloadAbortController.signal);

    const clearAbortController = () => {
      this.downloadAbortController = null;
    };

    fetchMethod
      .then(blob => {
        if (this.downloadCancelled || this.downloadAbortController!.signal.aborted) {
          throw new Error("Download cancelled");
        }
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(url);
        this.completeDownload("File downloaded successfully");
        this.toastr.success(`Started downloading: ${filename}`, "Success");
        clearAbortController();
      })
      .catch(error => {
        if (error.message === "Download cancelled" || error.name === "AbortError") {
          this.cancelDownload();
        } else {
          this.errorDownload("Failed to download file");
          this.toastr.error(`Failed to download: ${filename}`, "Error");
        }
        clearAbortController();
      });
  }

  private downloadWallpapersAsZip(wallpapers: any[]): void {
    this.initializeDownloadProgress(wallpapers.length);
    const zip = new JSZip();
    let completedCount = 0;
    const downloadPromises = wallpapers.map((wallpaper, index) => {
      if (!wallpaper.url) {
        return Promise.resolve();
      }

      return this.downloadService
        .fetchImageAsBlob(wallpaper.url)
        .then(blob => {
          if (this.downloadCancelled) {
            throw new Error("Download cancelled");
          }

          const filename = wallpaper.name || `wallpaper_${index + 1}.jpg`;
          zip.file(filename, blob);
          completedCount++;
          // Update progress
          this.updateDownloadProgress(completedCount, wallpapers.length, filename);
        })
        .catch(error => {
          if (this.downloadCancelled) {
            throw new Error("Download cancelled");
          }
          throw error;
        });
    });

    Promise.all(downloadPromises)
      .then(() => {
        if (this.downloadCancelled) {
          throw new Error("Download cancelled");
        }

        this.updateDownloadProgress(wallpapers.length, wallpapers.length, "Creating ZIP file...");
        return zip.generateAsync({ type: "blob" });
      })
      .then(content => {
        if (this.downloadCancelled) {
          throw new Error("Download cancelled");
        }

        const zipName = `wallpapers_${new Date().getTime()}.zip`;
        saveAs(content, zipName);
        this.completeDownload("ZIP file created successfully");
        this.toastr.success(`Started downloading: ${zipName}`, "Success");
      })
      .catch(error => {
        if (error.message === "Download cancelled") {
          this.cancelDownload();
        } else {
          this.errorDownload("Error downloading wallpapers. Please try again.");
          this.toastr.error("Error downloading wallpapers. Please try again.", "Error");
        }
      });
  }

  private downloadSingleWallpaper(wallpaper: any): void {
    if (!wallpaper.original) {
      this.toastr.error("Download URL not available", "Error");
      return;
    }

    this.initializeDownloadProgress(1);
    const filename = wallpaper.name || wallpaper.original.split("/").pop() || "wallpaper.jpg";

    // Use retry logic for better reliability
    this.downloadWallpaperWithRetry(wallpaper)
      .then(() => {
        this.completeDownload("Wallpaper downloaded successfully");
        this.toastr.success(`Started downloading: ${filename}`, "Success");
      })
      .catch(error => {
        if (error.message === "Download cancelled") {
          this.cancelDownload();
        } else {
          this.errorDownload("Failed to download wallpaper");
          // Fallback to direct download
          try {
            this.downloadService.downloadImage(wallpaper.original);
          } catch (fallbackError) {
            this.toastr.error(`Failed to download: ${filename}`, "Error");
          }
        }
      });
  }

  // Download progress helper methods
  private initializeDownloadProgress(totalFiles: number): void {
    this.downloadCancelled = false;
    this.downloadProgress = {
      current: 0,
      total: totalFiles,
      percentage: 0,
      currentFile: "Preparing download...",
      status: "downloading",
      message: "",
    };
    this.showDownloadProgress = true;
  }

  private updateDownloadProgress(current: number, total: number, currentFile: string, percent?: number): void {
    this.downloadProgress.current = current;
    this.downloadProgress.total = total;
    this.downloadProgress.percentage = percent !== undefined ? percent : Math.round((current / total) * 100);
    this.downloadProgress.currentFile = currentFile;
    this.downloadProgress.status = "downloading";
  }

  private completeDownload(message: string): void {
    this.downloadProgress.status = "completed";
    this.downloadProgress.message = message;
    this.downloadProgress.percentage = 100;

    // Auto-hide after 3 seconds
    setTimeout(() => {
      this.showDownloadProgress = false;
      this.downloadProgress.status = "idle";
    }, 3000);
  }

  private errorDownload(message: string): void {
    this.downloadProgress.status = "error";
    this.downloadProgress.message = message;

    // Auto-hide after 5 seconds
    setTimeout(() => {
      this.showDownloadProgress = false;
      this.downloadProgress.status = "idle";
    }, 5000);
  }

  private cancelDownload(): void {
    this.showDownloadProgress = false;
    this.downloadProgress.status = "idle";
    this.downloadCancelled = false;
  }

  private downloadWallpaperWithRetry(wallpaper: any, maxRetries: number = 3): Promise<void> {
    return new Promise((resolve, reject) => {
      let retryCount = 0;

      const attemptDownload = () => {
        if (this.downloadCancelled) {
          reject(new Error("Download cancelled"));
          return;
        }

        const filename = wallpaper.name || wallpaper.url.split("/").pop() || "wallpaper.jpg";
        this.updateDownloadProgress(1, 1, `Downloading ${filename}...`);

        this.downloadService
          .fetchImageAsBlob(wallpaper.url)
          .then(blob => {
            if (this.downloadCancelled) {
              reject(new Error("Download cancelled"));
              return;
            }

            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = downloadUrl;
            a.download = filename;
            a.click();
            window.URL.revokeObjectURL(downloadUrl);
            resolve();
          })
          .catch(error => {
            if (this.downloadCancelled) {
              reject(new Error("Download cancelled"));
              return;
            }

            retryCount++;
            if (retryCount < maxRetries) {
              this.updateDownloadProgress(1, 1, `Retrying download... (${retryCount}/${maxRetries})`);
              setTimeout(attemptDownload, 1000 * retryCount); // Exponential backoff
            } else {
              reject(new Error(`Failed to download after ${maxRetries} attempts: ${error.message}`));
            }
          });
      };

      attemptDownload();
    });
  }

  // Get recommendations by subcategory
  getRecommendationsBySubCategory(subCategory: string): any[] {
    if (!this.previewData || !this.previewData.recommendations) {
      return [];
    }

    return this.previewData.recommendations.filter((recommendation: any) => recommendation.subCategory === subCategory);
  }

  // Update brand color dynamically
  updateBrandColor(brandColor: string) {
    if (brandColor) {
      // Convert hex to RGB for rgba usage
      const hex = brandColor.replace("#", "");
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);

      // Set CSS variables on both document and component element
      document.documentElement.style.setProperty("--brand-color", brandColor);
      document.documentElement.style.setProperty("--brand-color-rgb", `${r}, ${g}, ${b}`);

      // Also set on the component element itself
      const componentElement = document.querySelector("app-preview");
      if (componentElement) {
        (componentElement as HTMLElement).style.setProperty("--brand-color", brandColor);
        (componentElement as HTMLElement).style.setProperty("--brand-color-rgb", `${r}, ${g}, ${b}`);
      }

      // Verify the CSS variable was set
      const computedStyle = getComputedStyle(document.documentElement);
      const actualColor = computedStyle.getPropertyValue("--brand-color");
    }
  }

  // Update brand font dynamically
  updateBrandFont(brandFont: string) {
    if (brandFont) {
      // Map font names to CSS variables
      const fontMapping: { [key: string]: string } = {
        Arial: "var(--font-arial)",
        Helvetica: "var(--font-helvetica)",
        "Times New Roman": "var(--font-times)",
        Georgia: "var(--font-georgia)",
        Verdana: "var(--font-verdana)",
        "Courier New": "var(--font-courier)",
        "Lucida Console": "var(--font-lucida)",
        "DejaVu Sans": "var(--font-dejavu-sans)",
        "DejaVu Serif": "var(--font-dejavu-serif)",
        Courier: "var(--font-courier-old)",
      };

      // Get the CSS variable for the font, or use the font name directly
      const fontValue = fontMapping[brandFont] || `'${brandFont}', sans-serif`;

      // Set CSS variable for brand font
      document.documentElement.style.setProperty("--brand-font", fontValue);

      // Also set on the component element itself
      const componentElement = document.querySelector("app-preview");
      if (componentElement) {
        (componentElement as HTMLElement).style.setProperty("--brand-font", fontValue);
      }

      // Verify the CSS variable was set
      const computedStyle = getComputedStyle(document.documentElement);
      const actualFont = computedStyle.getPropertyValue("--brand-font");
    }
  }

  // Navigate back to digital memory log list
  goBack() {
    const shipId = this.urlIds.overviewId; // This is just the numeric ID
    const shipName = this.route.snapshot.paramMap.get("shipName") || "ship";

    // Reconstruct the ship URL
    const shipUrl = `ship-${shipName}-${shipId}`;

    // Check the current URL to determine where to go back
    const currentUrl = this.router.url;

    if (currentUrl.includes("/preview-")) {
      // If we're in preview, go back to edit or create page
      // Thêm query param fromPreview để biết là quay lại từ preview
      if (this.queryParams.type === "create") {
        this.router.navigate(["/overview", shipUrl, `create-digital-memory-log-${this.urlIds.previewId}`], {
          queryParams: { fromPreview: "true" },
        });
      } else if (this.queryParams.type === "edit") {
        this.router.navigate(["/overview", shipUrl, `edit-digital-memory-log-${this.urlIds.previewId}`], {
          queryParams: { fromPreview: "true" },
        });
      } else {
        // Default: go to ship overview page
        this.router.navigate(["/overview", shipUrl]);
      }
    } else {
      // Default fallback
      this.router.navigate(["/overview", shipUrl]);
    }
  }

  onFileItemClick(fileItem: any, type: "file" | "photo" | "video" | "media", sectionIndex?: number, subIndex?: number) {
    if (!fileItem) {
      this.toastr.error("File item not available", "Error");
      return;
    }

    // Get the current preview ID and name from urlIds and route params
    const previewId = this.urlIds.previewId;
    const previewName = this.route.snapshot.paramMap.get("previewName") || "digital-memory";

    // If indices are not provided, try to find them (fallback for backward compatibility)
    if (sectionIndex === undefined || subIndex === undefined) {
      let sections = [];

      switch (type) {
        case "photo":
          sections = this.previewData.photoSections || [];
          break;
        case "video":
          sections = this.previewData.videoSections || [];
          break;
        case "media":
          sections = this.previewData.mediaSections || [];
          break;
        default:
          sections = this.previewData.fileSections || [];
      }

      // Find the section and subsection indices
      // Try to match by ID first, then by object reference, then by title
      for (let i = 0; i < sections.length; i++) {
        const section = sections[i];
        if (section.subSections && Array.isArray(section.subSections)) {
          const foundIndex = section.subSections.findIndex((sub: any) => {
            // Match by object reference (same object)
            if (sub === fileItem) {
              return true;
            }
            // Match by ID if both have IDs
            if (sub.id && fileItem.id && sub.id === fileItem.id) {
              return true;
            }
            // Match by title if no IDs (for new subsections)
            if (!sub.id && !fileItem.id && sub.title && fileItem.title && sub.title === fileItem.title) {
              return true;
            }
            return false;
          });
          if (foundIndex !== -1) {
            sectionIndex = i;
            subIndex = foundIndex;
            break;
          }
        }
      }

      // If still not found, log for debugging
      if (sectionIndex === undefined || subIndex === undefined) {
        console.warn("Could not find subsection indices:", {
          fileItem,
          type,
          sectionsLength: sections.length,
        });
        sectionIndex = -1;
        subIndex = -1;
      }
    }

    // Create file detail name (default to 'digital-memory' if no specific name available)
    const subSectionName = fileItem.name
      ? fileItem.name
          .toLowerCase()
          .replace(/[^a-z0-9\s-]/g, "")
          .replace(/\s+/g, "-")
          .replace(/-+/g, "-")
          .trim()
      : "digital-memory";

    // Use ID if available, otherwise use temporary ID based on indices
    const subSectionId = fileItem.id || `temp-${sectionIndex}-${subIndex}`;

    // Store navigation info for file-detail component to check
    const queryType = this.route.snapshot.queryParams["type"] || "";
    const navigationInfo = {
      fromPreview: true,
      previewId: previewId,
      timestamp: Date.now(),
      previewType: queryType, // Store whether it's from edit/create mode
      sectionIndex: sectionIndex,
      subIndex: subIndex,
      contentType: type, // Store content type (file/photo/video)
    };

    sessionStorage.setItem("previewNavigationInfo", JSON.stringify(navigationInfo));

    // Store current URL as previous URL
    sessionStorage.setItem("previousUrl", this.router.url);

    // Navigate using new URL pattern: preview-{name}-{id}/file-detail-{name}-{id}
    this.router.navigate([`preview-${previewName}-${previewId}`, `file-detail-${subSectionName}-${subSectionId}`], {
      relativeTo: this.route.parent, // Navigate relative to parent route
      queryParams: { type },
    });
  }

  private reloadLayoutAfterDataChange(): void {
    this.cdr.detectChanges();
    if (this.reloadMasonryLayout) {
      this.reloadMasonryLayout();
    }
  }

  private handleResize(): void {
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }

    this.resizeTimeout = setTimeout(() => {
      if (this.reloadMasonryLayout) {
        this.reloadMasonryLayout();
      }
    }, 250);
  }

  /**
   * Kiểm tra xem có content nào được hiển thị không
   */
  private checkContentAvailability(): boolean {
    if (!this.previewData) {
      return false;
    }

    // Kiểm tra từng display flag và data tương ứng
    const displayChecks = [
      // Banner
      this.previewData.displayBanner && this.previewData.banner,

      // Brand Logo - chỉ cần displayBrandLogo = true
      this.previewData.displayBrandLogo,

      // Description
      this.previewData.displayDescription && this.previewData.description,

      // Inspirational Video
      this.previewData.displayInspirationalVideo && this.previewData.inspirationalVideo,

      // Courses
      this.previewData.displayCourses && this.previewData.courses && this.previewData.courses.length > 0,

      // Files
      this.previewData.displayFiles && this.previewData.fileSections && this.previewData.fileSections.length > 0,

      // Media
      this.previewData.displayMedias && this.previewData.mediaSections && this.previewData.mediaSections.length > 0,

      // Photos
      this.previewData.displayPhotos && this.previewData.photoSections && this.previewData.photoSections.length > 0,

      // Videos
      this.previewData.displayVideos && this.previewData.videoSections && this.previewData.videoSections.length > 0,

      // Wallpapers
      this.previewData.displayWallpapers && this.previewData.wallpapers && this.previewData.wallpapers.length > 0,

      // Animal POI
      this.previewData.displayAnimalPOI && this.previewData.animalPOI,

      // Recommendations
      this.previewData.displayRecommendations &&
        this.previewData.recommendations &&
        this.previewData.recommendations.length > 0,
    ];

    // Trả về true nếu có ít nhất một content được hiển thị
    return displayChecks.some(check => check === true);
  }

  /**
   * Kiểm tra xem tất cả display flags có false không
   */
  private areAllDisplayFlagsFalse(): boolean {
    if (!this.previewData) {
      return true;
    }

    const displayFlags = [
      this.previewData.displayAnimalPOI,
      this.previewData.displayBanner,
      this.previewData.displayBrandLogo,
      this.previewData.displayCourses,
      this.previewData.displayDescription,
      this.previewData.displayFiles,
      this.previewData.displayMedias,
      this.previewData.displayInspirationalVideo,
      this.previewData.displayPhotos,
      this.previewData.displayRecommendations,
      this.previewData.displayVideos,
      this.previewData.displayWallpapers,
    ];

    return displayFlags.every(flag => flag === false);
  }

  /**
   * Cập nhật trạng thái content availability
   */
  private updateContentAvailability(): void {
    this.hasContent = this.checkContentAvailability();
  }

  get translationBtnText(): string {
    return AppShareHelper.translationButtonText({
      originalComment: this.isTranslated ? true : null,
    });
  }

  async toggleTranslation() {
    if (this.translating || !this.previewData) return;

    if (this.isTranslated) {
      this.previewData = JSON.parse(JSON.stringify(this.originalPreviewData));
      this.wallpapers = this.previewData.wallpapers || [];
      this.isTranslated = false;
      this.cdr.detectChanges();
      return;
    }

    this.translating = true;
    try {
      this.originalPreviewData = JSON.parse(JSON.stringify(this.previewData));

      const entries: Array<{ text: string; apply: (translated: string) => void }> = [];
      const addEntry = (text: string, apply: (t: string) => void) => {
        if (text) entries.push({ text, apply });
      };

      addEntry(this.previewData.description, t => (this.previewData.description = t));
      addEntry(this.previewData.titlePhotoSections, t => (this.previewData.titlePhotoSections = t));
      addEntry(this.previewData.titleVideoSections, t => (this.previewData.titleVideoSections = t));
      addEntry(this.previewData.titleFileSections, t => (this.previewData.titleFileSections = t));

      (this.previewData.photoSections || []).forEach((section: any, si: number) => {
        addEntry(section.name, t => (this.previewData.photoSections[si].name = t));
        (section.subSections || []).forEach((sub: any, ssi: number) => {
          addEntry(sub.title, t => (this.previewData.photoSections[si].subSections[ssi].title = t));
        });
      });

      (this.previewData.videoSections || []).forEach((section: any, si: number) => {
        addEntry(section.name, t => (this.previewData.videoSections[si].name = t));
        (section.subSections || []).forEach((sub: any, ssi: number) => {
          addEntry(sub.title, t => (this.previewData.videoSections[si].subSections[ssi].title = t));
        });
      });

      (this.previewData.fileSections || []).forEach((section: any, si: number) => {
        addEntry(section.name, t => (this.previewData.fileSections[si].name = t));
        (section.subSections || []).forEach((sub: any, ssi: number) => {
          addEntry(sub.title, t => (this.previewData.fileSections[si].subSections[ssi].title = t));
        });
      });

      (this.previewData.mediaSections || []).forEach((section: any, si: number) => {
        addEntry(section.name, t => (this.previewData.mediaSections[si].name = t));
        (section.subSections || []).forEach((sub: any, ssi: number) => {
          addEntry(sub.title, t => (this.previewData.mediaSections[si].subSections[ssi].title = t));
        });
      });

      (this.previewData.courses || []).forEach((course: any, ci: number) => {
        addEntry(course.name, t => (this.previewData.courses[ci].name = t));
        addEntry(course.description, t => (this.previewData.courses[ci].description = t));
      });

      (this.previewData.recommendations || []).forEach((rec: any, ri: number) => {
        addEntry(rec.title, t => (this.previewData.recommendations[ri].title = t));
        addEntry(rec.description, t => (this.previewData.recommendations[ri].description = t));
      });

      if (entries.length === 0) {
        this.toastr.info("No text content to translate.", "Info");
        this.translating = false;
        return;
      }

      const targetLang = (navigator.language || "en").split("-")[0];
      await this.translateService.translateFieldEntries(entries, targetLang).toPromise();

      this.isTranslated = true;
      this.cdr.detectChanges();
    } catch (error) {
      console.error("Translation error:", error);
      if (this.originalPreviewData) {
        this.previewData = JSON.parse(JSON.stringify(this.originalPreviewData));
        this.wallpapers = this.previewData.wallpapers || [];
      }
      this.toastr.error("Translation failed. Please try again.", "Error");
    } finally {
      this.translating = false;
    }
  }

  ngOnDestroy() {
    window.removeEventListener("resize", this.handleResize.bind(this));
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
  }
}
